/**
 * Focus management for overlay panels (command palette, attachment viewer,
 * in-report find, and the mobile nav sheet to come): traps Tab/Shift+Tab
 * inside the panel, closes on Escape from anywhere (not just a focused
 * input), and restores focus to the element that opened the overlay once
 * it closes.
 *
 * Most overlays mount/unmount with the panel (CommandPalette, the attachment
 * Viewer), so `active` defaults to true. ReportNext's find bar instead stays
 * mounted in its parent and toggles visibility, so it passes `active:
 * findOpen` to re-run the save/restore cycle on each open.
 */
import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The panel's actually-tabbable elements. The selector alone is not enough:
 * it matches display:none controls (the palette's mobile-only ✕), and a
 * trap keyed on an invisible "last" never wraps.
 */
function tabbables(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.getClientRects().length > 0,
  );
}

// Stack of currently-active instances, in mount order. Overlays can nest
// (the menu sheet can open over the mobile contents list, both above the
// still-interactive masthead), and every instance listens for Escape on
// document; without the stack one press would close them all at once and
// the focus restores would race. Only the topmost instance may act.
const stack: symbol[] = [];

export function useModalFocus(
  panelRef: RefObject<HTMLElement | null>,
  options: {
    /** Escape anywhere while the panel is active runs this. */
    onClose?: () => void;
    /** Wrap Tab/Shift+Tab at the panel's first/last focusable element. */
    trap?: boolean;
    /** Return focus to the invoking element once the panel deactivates. */
    restore?: boolean;
    /** Whether the panel is currently open. Defaults to true. */
    active?: boolean;
  } = {},
): void {
  const { onClose, trap = true, restore = true, active = true } = options;
  const invoker = useRef<HTMLElement | null>(null);
  const id = useRef<symbol | null>(null);
  if (id.current === null) id.current = Symbol("modal");

  useEffect(() => {
    if (!active) return;
    invoker.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const first = panel ? tabbables(panel)[0] : undefined;
    if (first) {
      first.focus();
    } else if (panel) {
      panel.tabIndex = -1;
      panel.focus();
    }
    return () => {
      if (restore && invoker.current?.isConnected) invoker.current.focus();
    };
    // Re-runs only when `active` flips; re-running on every render would
    // re-focus the panel and fight the user's own tabbing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Stack membership keys on `active` alone: the keydown effect below re-runs
  // whenever an inline onClose identity changes, and re-pushing there would
  // let a mere re-render hoist a buried overlay back above a newer one.
  useEffect(() => {
    if (!active) return;
    const me = id.current!;
    stack.push(me);
    return () => {
      const at = stack.indexOf(me);
      if (at !== -1) stack.splice(at, 1);
    };
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const me = id.current!;
    const onKeyDown = (e: KeyboardEvent) => {
      // Only the topmost active overlay handles keys; a stacked one
      // underneath waits its turn.
      if (stack[stack.length - 1] !== me) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (!trap || e.key !== "Tab") return;
      const panel = panelRef.current;
      const items = panel ? tabbables(panel) : [];
      if (!items.length) {
        // A modal with nothing tabbable in it yet (still loading, or a notice
        // with no controls) must still HOLD the tab. Letting Tab through sent
        // focus to the page behind an open, focus-trapping dialog. The panel
        // itself carries tabIndex=-1 from the mount effect, so it is a valid
        // place to keep focus until something better appears.
        e.preventDefault();
        panel?.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      const idx = active ? items.indexOf(active) : -1;
      if (idx !== -1) {
        if (e.shiftKey && idx === 0) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && idx === items.length - 1) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      // Focus is not on a known tabbable. Chrome puts scrollable regions
      // without focusable children into the tab order (the palette listbox,
      // the viewer body), so the panel can hold implicit stops the selector
      // cannot see. Contain those too: wrap only when the browser's default
      // move would walk out of the panel (no tabbable follows/precedes the
      // implicit stop in DOM order); otherwise let it proceed naturally.
      if (!active || !panel || !panel.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      const follows = items.some(
        (el) => active.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING,
      );
      const precedes = items.some(
        (el) => active.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING,
      );
      if (!e.shiftKey && !follows) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && !precedes) {
        e.preventDefault();
        last.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [active, onClose, trap, panelRef]);
}
